import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ADMIN_UPLOAD_STATE_EVENT, getToken, getRole, setToken } from './api';
import { navButtonClass, mobileTabClass } from './adminUi';

const NAV_SECTIONS = [
  {
    title: 'Homepage',
    items: [
      { to: '/admin/dashboard/header', label: 'Header', shortLabel: 'Header', adminOnly: true },
      { to: '/admin/dashboard/hero', label: 'Hero', shortLabel: 'Hero', adminOnly: true },
      { to: '/admin/dashboard/about', label: 'About', shortLabel: 'About', adminOnly: true },
      { to: '/admin/dashboard/history', label: 'History', shortLabel: 'History', adminOnly: true },
      {
        to: '/admin/dashboard/farmer',
        label: 'Farmer story (/farmer)',
        shortLabel: 'Farmer',
        adminOnly: true,
      },
      {
        to: '/admin/dashboard/founder-desk',
        label: "Founder's desk",
        shortLabel: 'Founder',
        adminOnly: true,
      },
      {
        to: '/admin/dashboard/sustainable-farming',
        label: 'Sustainable farming',
        shortLabel: 'Farming',
        adminOnly: true,
      },
      {
        to: '/admin/dashboard/brand-ticker',
        label: 'Agri Intelligence',
        shortLabel: 'Agri',
        adminOnly: true,
      },
      {
        to: '/admin/dashboard/impact-stats',
        label: 'Impact stats',
        shortLabel: 'Impact',
        adminOnly: true,
      },
    ],
  },
  {
    title: 'Content',
    items: [
      { to: '/admin/dashboard/news', label: 'News', shortLabel: 'News' },
      { to: '/admin/dashboard/products', label: 'Products', shortLabel: 'Products' },
      {
        to: '/admin/dashboard/seed-varieties',
        label: 'Variety products',
        shortLabel: 'Varieties',
      },
      {
        to: '/admin/dashboard/seed-varieties/categories',
        label: 'Variety categories',
        shortLabel: 'Var. cat.',
      },
      {
        to: '/admin/dashboard/gallery',
        label: 'All gallery',
        shortLabel: 'Gallery',
        children: [{ to: '/admin/dashboard/gallery/albums', label: 'Create album', shortLabel: 'Album' }],
      },
    ],
  },
  {
    title: 'Account',
    items: [{ to: '/admin/dashboard/password', label: 'Password', shortLabel: 'Password' }],
  },
  {
    title: 'Site',
    items: [
      { to: '/admin/dashboard/seo', label: 'SEO & meta', shortLabel: 'SEO', adminOnly: true },
      { to: '/admin/dashboard/footer', label: 'Footer', shortLabel: 'Footer', adminOnly: true },
      {
        to: '/admin/dashboard/users',
        label: 'Team & editors',
        shortLabel: 'Team',
        adminOnly: true,
      },
    ],
  },
];

const NAV_ICON_BY_PATH = {
  '/admin/dashboard/header': 'header',
  '/admin/dashboard/hero': 'hero',
  '/admin/dashboard/about': 'about',
  '/admin/dashboard/history': 'history',
  '/admin/dashboard/farmer': 'farmer',
  '/admin/dashboard/founder-desk': 'founder',
  '/admin/dashboard/sustainable-farming': 'farming',
  '/admin/dashboard/brand-ticker': 'ticker',
  '/admin/dashboard/impact-stats': 'impact',
  '/admin/dashboard/news': 'news',
  '/admin/dashboard/products': 'products',
  '/admin/dashboard/seed-varieties': 'varieties',
  '/admin/dashboard/seed-varieties/categories': 'varcat',
  '/admin/dashboard/gallery': 'gallery',
  '/admin/dashboard/gallery/albums': 'gallery',
  '/admin/dashboard/password': 'password',
  '/admin/dashboard/seo': 'seo',
  '/admin/dashboard/footer': 'footer',
  '/admin/dashboard/users': 'users',
};

function isPathActive(pathname, to) {
  if (pathname === to) return true;
  if (!pathname.startsWith(`${to}/`)) return false;
  if (to === '/admin/dashboard/seed-varieties' && pathname.startsWith('/admin/dashboard/seed-varieties/categories')) {
    return false;
  }
  if (to === '/admin/dashboard/gallery' && pathname.startsWith('/admin/dashboard/gallery/albums')) {
    return false;
  }
  return true;
}

function flattenNavItems(items) {
  const flat = [];
  for (const item of items) {
    flat.push(item);
    if (Array.isArray(item.children) && item.children.length) {
      flat.push(...item.children);
    }
  }
  return flat;
}

function ActiveNavHeartIcon({ className = '' }) {
  return (
    <svg
      className={['h-4 w-4 shrink-0 text-white', className].filter(Boolean).join(' ')}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}

function SidebarIcon({ name, className }) {
  switch (name) {
    case 'header':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M4 9h16" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'hero':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M8 14l2.2-2.2 1.8 1.8 3-3L18 14" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'about':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'history':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 8v5l3 2M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'farmer':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M12 20V10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <path d="M9 13c-2.2 0-4-1.5-4-3.5 2.2 0 4 1.5 4 3.5z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M15 16c2.2 0 4-1.5 4-3.5-2.2 0-4 1.5-4 3.5z" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'founder':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="1.7" />
          <path d="M6 18c1.4-2.5 3.5-4 6-4s4.6 1.5 6 4" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'farming':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M12 20V9" stroke="currentColor" strokeWidth="1.7" />
          <path d="M12 12c-3 0-5-2-5-5 3 0 5 2 5 5zM12 15c3 0 5-2 5-5-3 0-5 2-5 5z" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'ticker':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="4" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M7 10h6M7 14h10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case 'impact':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M6 17V12M12 17V8M18 17V10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case 'news':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M8 10h8M8 13h8M8 16h5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case 'products':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="5" y="7" width="14" height="11" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M9 7V5h6v2" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'varieties':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M12 4c-2 3-6 3.5-6 8a6 6 0 1012 0c0-4.5-4-5-6-8z"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinejoin="round"
          />
          <path d="M12 14v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case 'varcat':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M5 7h14v10H5V7z" stroke="currentColor" strokeWidth="1.7" />
          <path d="M9 11h6M9 14h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case 'gallery':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <circle cx="9" cy="10" r="1.4" fill="currentColor" />
          <path d="M7 17l4-4 2.6 2.6L17 12l3 3" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'password':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="5" y="11" width="14" height="8" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M8 11V9a4 4 0 018 0v2" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'seo':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="11" cy="11" r="5.5" stroke="currentColor" strokeWidth="1.7" />
          <path d="M15.5 15.5L19 19" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      );
    case 'footer':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <rect x="4" y="5" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M4 15h16" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    case 'users':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.7" />
          <circle cx="16" cy="10" r="2" stroke="currentColor" strokeWidth="1.7" />
          <path d="M4.5 18c1-2.1 2.8-3.2 4.5-3.2 1.8 0 3.5 1.1 4.5 3.2" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
    default:
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" />
        </svg>
      );
  }
}

function NavGroup({ title, children }) {
  return (
    <div className="mt-5 first:mt-0">
      <p className="mb-2 px-3 text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-neutral-400">
        {title}
      </p>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [uploading, setUploading] = useState(false);
  const isAdmin = getRole() !== 'editor';
  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.adminOnly || isAdmin),
  })).filter((section) => section.items.length > 0);
  const navItems = visibleSections.flatMap((section) => flattenNavItems(section.items));
  const currentItem = navItems.find((item) => isPathActive(pathname, item.to));

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    function onUploadState(e) {
      setUploading(Boolean(e?.detail?.active));
    }
    window.addEventListener(ADMIN_UPLOAD_STATE_EVENT, onUploadState);
    return () => {
      window.removeEventListener(ADMIN_UPLOAD_STATE_EVENT, onUploadState);
    };
  }, []);

  useEffect(() => {
    if (!uploading) return;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [uploading]);

  function logout() {
    setToken(null);
    navigate('/admin', { replace: true });
  }

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-neutral-100 md:flex-row">
      <aside className="hidden shrink-0 border-b border-neutral-200 bg-white md:flex md:h-[100dvh] md:w-72 md:flex-col md:overflow-y-auto md:border-b-0 md:border-r md:[scrollbar-width:thin] md:[scrollbar-color:#9ca3af_transparent] md:[&::-webkit-scrollbar]:w-2 md:[&::-webkit-scrollbar-track]:bg-transparent md:[&::-webkit-scrollbar-thumb]:rounded-full md:[&::-webkit-scrollbar-thumb]:bg-neutral-400 md:[&::-webkit-scrollbar-thumb:hover]:bg-neutral-500">
        <div className="flex flex-1 flex-col p-5">
          <div className="mb-6 rounded-2xl border border-neutral-200 bg-gradient-to-br from-white to-neutral-50 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-olive">Rafid Seed</p>
            <h1 className="mt-1 text-lg font-bold tracking-tight text-neutral-900">
              {isAdmin ? 'Admin' : 'Content editor'}
            </h1>
            {!isAdmin ? (
              <p className="mt-1 text-xs text-neutral-500">Manage news, products, variety specs, and gallery</p>
            ) : null}
          </div>
          <nav className="flex flex-col" aria-label="Content sections">
            {visibleSections.map((section) => (
              <NavGroup key={section.title} title={section.title}>
                {section.items.map((item) => {
                  const active = isPathActive(pathname, item.to);
                  return (
                    <div key={item.to}>
                      <NavLink to={item.to} className={() => navButtonClass(active)}>
                        <span className="flex items-center justify-between gap-3">
                          <span className="flex min-w-0 items-center gap-2.5">
                            <span
                              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ring-1 transition-colors ${
                                active
                                  ? 'bg-white/15 text-white ring-white/20'
                                  : 'bg-neutral-100 text-neutral-500 ring-neutral-200 group-hover:bg-white group-hover:text-brand-forest group-hover:ring-neutral-300'
                              }`}
                            >
                              {active ? (
                                <ActiveNavHeartIcon />
                              ) : (
                                <SidebarIcon name={NAV_ICON_BY_PATH[item.to]} className="h-4 w-4" />
                              )}
                            </span>
                            <span className="truncate text-[0.92rem] font-semibold tracking-tight">{item.label}</span>
                          </span>
                        </span>
                      </NavLink>
                      {Array.isArray(item.children) && item.children.length ? (
                        <div className="mt-1 space-y-1 pl-9">
                          {item.children.map((child) => {
                            const childActive = isPathActive(pathname, child.to);
                            return (
                              <NavLink
                                key={child.to}
                                to={child.to}
                                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                  childActive
                                    ? 'bg-brand-forest/10 text-brand-forest'
                                    : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                                }`}
                              >
                                {child.label}
                              </NavLink>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </NavGroup>
            ))}
          </nav>
        </div>
        <div className="border-t border-neutral-100 bg-white p-4">
          <p className="mb-2 text-xs text-neutral-500">{isAdmin ? 'Full access account' : 'Limited access account'}</p>
          <button
            type="button"
            onClick={logout}
            className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            Log out
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-neutral-100">
        <div className="flex shrink-0 flex-col gap-2 border-b border-neutral-200 bg-white p-3 md:hidden">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold text-neutral-800">Admin workspace</p>
            {currentItem ? <p className="text-xs font-medium text-neutral-500">{currentItem.label}</p> : null}
          </div>
          <div className="overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={() => mobileTabClass(isPathActive(pathname, item.to))}
                >
                  {item.shortLabel}
                </NavLink>
              ))}
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="w-full rounded-xl border border-neutral-200 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            Log out
          </button>
        </div>

        <div className="hidden shrink-0 items-center justify-between border-b border-neutral-200 bg-white px-6 py-3 md:flex">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">Mainbar</p>
            <h2 className="text-base font-semibold text-neutral-900">
              {currentItem ? currentItem.label : 'Dashboard'}
            </h2>
          </div>
          <p className="text-xs text-neutral-500">{isAdmin ? 'Administrator access' : 'Editor access'}</p>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-0 md:p-4">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white md:rounded-2xl md:border md:border-neutral-200 md:shadow-sm">
            <Outlet />
          </div>
        </div>
      </div>
      {uploading ? (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-neutral-950/35 px-4 backdrop-blur-[1px]">
          <div className="w-full max-w-xs rounded-2xl border border-neutral-200 bg-white px-5 py-4 text-center shadow-xl">
            <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-brand-olive" />
            <p className="text-sm font-semibold text-neutral-900">Uploading image...</p>
            <p className="mt-1 text-xs text-neutral-500">Please wait. Editing is temporarily locked.</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
